import { useEffect, useRef, useState } from 'react'
import { Link, useRouter } from '@tanstack/react-router'

import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import {
  LedgerBadge,
  LedgerTable,
  type LedgerBadgeTone,
  type LedgerColumn,
} from '@/components/ui/LedgerTable'
import {
  CustomerConversation,
  type FollowUpThread,
  type ThreadTarget,
} from '@/components/CustomerConversation'
import { formatShortRelativeDateTime } from '@/lib/relativeDateTime'
import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import { NewFollowUpDialog } from '@/components/NewFollowUpDialog'
import {
  CustomerHistory,
  type CustomerHistoryEvent,
  type HistoryLink,
} from '@/components/CustomerHistory'
import type {
  NewFollowUpInput,
  ScheduleStep,
} from '@/db/domain/followUps'
import type {
  PhotoNoteReplace,
  PhotoNoteSubmit,
} from '@/components/PhotoNoteComposer'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import { ALL_SERVICE_STATUSES } from '@/db/domain/serviceStatusOrder'
import { formatServiceStatus } from '@/help/serviceStatus'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import { RevisionsCard } from '@/components/RevisionsCard'
import type { Callout, Customer, CustomerRevision, Job, StatusChangeLog } from '@/lib/schemas/db'

type CustomerServiceType = 'Callout' | 'Session' | 'Job'

const serviceDetailRoute: Record<
  CustomerServiceType,
  '/callouts/$id' | '/sessions/$id' | '/jobs/$id'
> = {
  Callout: '/callouts/$id',
  Session: '/sessions/$id',
  Job: '/jobs/$id',
}

const serviceTypeTone: Record<CustomerServiceType, LedgerBadgeTone> = {
  Callout: 'blue',
  Session: 'violet',
  Job: 'amber',
}

const saveButtonClasses = (active: boolean) =>
  [
    'inline-flex h-8 items-center rounded-md px-3 text-[11px] font-medium',
    active
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-slate-100 text-slate-500',
  ].join(' ')

const resetButtonClasses = (active: boolean) =>
  [
    'inline-flex h-8 items-center rounded-md px-3 text-[11px] font-medium',
    active
      ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      : 'bg-slate-100 text-slate-400',
  ].join(' ')

interface ServiceRow {
  id: string
  startTime: string
  isWholeDay: boolean
  issueSummary: string
  status: Callout['status'] | Job['status']
  serviceType: CustomerServiceType
  statusChanges?: StatusChangeLog[]
}

/** One file on the Documents tab, flattened for display. */
export interface CustomerDocumentRow {
  id: string
  title: string
  fileUrl?: string
  size?: number
  uploadedByName: string
  createdAt: string
  /** Still uploading — no server URL to link to yet. */
  pending?: boolean
}

function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`
}

function bookedLabel(service: ServiceRow): string {
  const relative = formatRelativeDateTime(service.startTime)
  return service.isWholeDay ? `${relative} · All day` : relative
}

export interface CustomerDetailsScreenProps {
  customer: Customer
  services: ServiceRow[]
  /** Archived services failed to load — the list shows active services only. */
  archivedServicesError?: boolean
  activeTab?: 'details' | 'services' | 'notes' | 'history' | 'documents'
  getAuthorDisplayName?: (authorId: string) => string
  /**
   * The conversation view: follow-up threads, service threads and general
   * notes, rendered chat-style by {@link CustomerConversation}.
   */
  followUpThreads?: FollowUpThread[]
  generalNotes?: CalloutNotesSectionNote[]
  /** The audit trail behind the History tab, strictly chronological. */
  historyEvents?: CustomerHistoryEvent[]
  /** Open a history row's entity: a thread on the notes tab, or a service. */
  onOpenHistoryLink?: (link: HistoryLink) => void
  /** Switch to the History tab (the conversation links to it). */
  onOpenHistory?: () => void
  onCompleteFollowUp?: (id: string, options: { note?: string }) => void
  onSnoozeFollowUp?: (
    id: string,
    step: ScheduleStep,
    options: { note?: string },
  ) => void
  onRescheduleFollowUp?: (
    id: string,
    dueAt: string,
    options: { note?: string },
  ) => void
  onCloseFollowUp?: (id: string, options: { note?: string }) => void
  onReopenFollowUp?: (
    id: string,
    dueAt: string,
    options: { note?: string },
  ) => void
  /** Raise a new follow-up for this customer. */
  onCreateFollowUp?: (
    input: NewFollowUpInput,
    extras: { firstNote?: string },
  ) => void
  /** Staff who can be assigned, passed through to the follow-up dialog. */
  assigneeOptions?: Array<{ id: string; name: string }>
  categoryOptions?: Array<{ id: string; name: string }>
  /** Selected thread key, kept in the URL by the container for deep links. */
  selectedThreadKey?: string
  onSelectThread?: (key: string) => void
  newNoteAuthorName?: string
  /** Add a note to the selected thread — general, follow-up, or service. */
  onAddNote?: (text: string, target: ThreadTarget) => boolean
  onSubmitPhotoNote?: (input: PhotoNoteSubmit, target: ThreadTarget) => void
  onReplacePhotoNote?: (
    id: string,
    input: PhotoNoteReplace,
    target: ThreadTarget,
  ) => void
  onSaveEditNote?: (id: string, text: string, target: ThreadTarget) => void
  onDeleteNote?: (id: string, target: ThreadTarget) => void
  photoNoteError?: string | null
  onClearPhotoNoteError?: () => void
  /** The Documents tab's list, live documents only, newest first. */
  documents?: CustomerDocumentRow[]
  /** Upload the picked files, each as its own document. */
  onUploadDocuments?: (files: File[]) => void
  /**
   * Open a document in a new tab. Goes through the container because the
   * stored files are protected — each open needs a fresh file token.
   */
  onOpenDocument?: (doc: CustomerDocumentRow) => void
  /** Soft-delete a document (its file stays stored). */
  onDeleteDocument?: (id: string) => void
  /** Error to surface above the list (e.g. a failed upload). */
  documentError?: string | null
  onClearDocumentError?: () => void
  onSaveCustomerDetails?: (values: {
    fullName: string
    externalId: string
    contactEmail: string
    contactPhone: string
    contactAddress: string
    invoiceName: string
    invoiceEmail: string
    invoicePhone: string
    invoiceAddress: string
    invoiceVat: string
  }) => void | Promise<void>
  /** The details form's add-only edit trail, oldest first. */
  revisions?: CustomerRevision[]
  /** Opens the archive dialog (absent = no archive action offered). */
  onArchive?: () => void
  onRestore?: () => void | Promise<void>
  /** The customer this one's resources were relinked to. */
  mergedInto?: { routeId: string; name: string }
}

export function CustomerDetailsScreen({
  customer,
  services,
  archivedServicesError,
  activeTab = 'details',
  getAuthorDisplayName,
  followUpThreads = [],
  generalNotes = [],
  historyEvents = [],
  onOpenHistoryLink,
  onOpenHistory,
  onCompleteFollowUp,
  onSnoozeFollowUp,
  onRescheduleFollowUp,
  onCloseFollowUp,
  onReopenFollowUp,
  onCreateFollowUp,
  assigneeOptions,
  categoryOptions,
  selectedThreadKey,
  onSelectThread,
  newNoteAuthorName = '',
  onAddNote,
  onSubmitPhotoNote,
  onReplacePhotoNote,
  onSaveEditNote,
  onDeleteNote,
  photoNoteError,
  onClearPhotoNoteError,
  documents = [],
  onUploadDocuments,
  onOpenDocument,
  onDeleteDocument,
  documentError,
  onClearDocumentError,
  onSaveCustomerDetails,
  revisions = [],
  onArchive,
  onRestore,
  mergedInto,
}: CustomerDetailsScreenProps) {
  const router = useRouter()
  const [isCreatingFollowUp, setIsCreatingFollowUp] = useState(false)
  const documentInputRef = useRef<HTMLInputElement | null>(null)
  // Two-click delete: the first click arms this row, the second confirms.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  )
  const [customerDraft, setCustomerDraft] = useState({
    fullName: customer.fullName ?? '',
    externalId: customer.externalId ?? '',
    contactEmail: customer.contactEmail ?? '',
    contactPhone: customer.contactPhone ?? '',
    contactAddress: customer.contactAddress ?? '',
    invoiceName: customer.invoiceName ?? customer.fullName ?? '',
    invoiceEmail: customer.invoiceEmail ?? customer.contactEmail ?? '',
    invoicePhone: customer.invoicePhone ?? customer.contactPhone ?? '',
    invoiceAddress: customer.invoiceAddress ?? customer.contactAddress ?? '',
    invoiceVat: customer.invoiceVat ?? '',
  })
  const [isSavingCustomer, setIsSavingCustomer] = useState(false)

  const baselineDraft = {
    fullName: customer.fullName ?? '',
    externalId: customer.externalId ?? '',
    contactEmail: customer.contactEmail ?? '',
    contactPhone: customer.contactPhone ?? '',
    contactAddress: customer.contactAddress ?? '',
    invoiceName: customer.invoiceName ?? customer.fullName ?? '',
    invoiceEmail: customer.invoiceEmail ?? customer.contactEmail ?? '',
    invoicePhone: customer.invoicePhone ?? customer.contactPhone ?? '',
    invoiceAddress: customer.invoiceAddress ?? customer.contactAddress ?? '',
    invoiceVat: customer.invoiceVat ?? '',
  }

  useEffect(() => {
    setCustomerDraft(baselineDraft)
  }, [
    customer.id,
    customer.fullName,
    customer.externalId,
    customer.contactEmail,
    customer.contactPhone,
    customer.contactAddress,
    customer.invoiceName,
    customer.invoiceEmail,
    customer.invoicePhone,
    customer.invoiceAddress,
    customer.invoiceVat,
  ])

  const customerDraftDirty =
    customerDraft.fullName !== baselineDraft.fullName ||
    customerDraft.externalId !== baselineDraft.externalId ||
    customerDraft.contactEmail !== baselineDraft.contactEmail ||
    customerDraft.contactPhone !== baselineDraft.contactPhone ||
    customerDraft.contactAddress !== baselineDraft.contactAddress ||
    customerDraft.invoiceName !== baselineDraft.invoiceName ||
    customerDraft.invoiceEmail !== baselineDraft.invoiceEmail ||
    customerDraft.invoicePhone !== baselineDraft.invoicePhone ||
    customerDraft.invoiceAddress !== baselineDraft.invoiceAddress ||
    customerDraft.invoiceVat !== baselineDraft.invoiceVat

  const handleResetCustomerDetails = () => {
    setCustomerDraft(baselineDraft)
  }

  const handleSaveCustomerDetails = async () => {
    if (!customerDraftDirty || !onSaveCustomerDetails || isSavingCustomer) return
    setIsSavingCustomer(true)
    try {
      await Promise.resolve(onSaveCustomerDetails(customerDraft))
    } finally {
      setIsSavingCustomer(false)
    }
  }

  const customerRouteId = customer.publicId ?? customer.id

  // The full list, matching the Services ledger — a customer's history is a
  // small sample, so deriving options from it left most statuses unpickable.
  const statusFilterOptions = ALL_SERVICE_STATUSES.map((status) => ({
    value: status,
    label: formatServiceStatus(status),
  }))

  const serviceColumns: LedgerColumn<ServiceRow>[] = [
    {
      key: 'ref',
      label: 'Ref',
      widthClass: 'w-28',
      sortValue: (service) => service.id,
      filter: 'text',
      filterValue: (service) => service.id,
      render: (service) => (
        <Link
          to={serviceDetailRoute[service.serviceType]}
          params={{ id: service.id }}
          title="Open service"
          className="font-mono text-[12.5px] whitespace-nowrap text-blue-700 hover:underline"
        >
          {service.id}
        </Link>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      widthClass: 'w-28',
      sortValue: (service) => service.serviceType,
      filter: 'select',
      filterOptions: (['Callout', 'Session', 'Job'] as const).map((type) => ({
        value: type,
        label: type,
      })),
      filterValue: (service) => service.serviceType,
      render: (service) => (
        <LedgerBadge tone={serviceTypeTone[service.serviceType]}>
          {service.serviceType}
        </LedgerBadge>
      ),
    },
    {
      key: 'issue',
      label: 'Issue',
      filter: 'text',
      filterValue: (service) => service.issueSummary,
      cellClass: 'text-slate-600',
      render: (service) => (
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          {service.issueSummary || '—'}
        </div>
      ),
    },
    {
      key: 'booked',
      label: 'Booked',
      widthClass: 'w-40',
      sortValue: (service) => service.startTime,
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (service) => bookedLabel(service),
    },
    {
      key: 'status',
      label: 'Status',
      widthClass: 'w-40',
      sortValue: (service) => service.status,
      filter: 'select',
      filterOptions: statusFilterOptions,
      filterValue: (service) => service.status,
      cellClass: 'whitespace-nowrap',
      render: (service) => (
        <StatusHistoryPill
          variant="ledger"
          currentStatus={service.status}
          statusChanges={service.statusChanges}
          getAuthorDisplayName={getAuthorDisplayName}
        />
      ),
    },
  ]

  const openService = (service: ServiceRow) => {
    router.navigate({
      to: serviceDetailRoute[service.serviceType],
      params: { id: service.id },
    })
  }

  const documentColumns: LedgerColumn<CustomerDocumentRow>[] = [
    {
      key: 'title',
      label: 'Document',
      filter: 'text',
      filterValue: (doc) => doc.title,
      sortValue: (doc) => doc.title.toLowerCase(),
      render: (doc) =>
        doc.fileUrl && onOpenDocument ? (
          <button
            type="button"
            title="Open document"
            onClick={(event) => {
              event.stopPropagation()
              onOpenDocument(doc)
            }}
            className="cursor-pointer border-b border-blue-200 text-blue-700 hover:border-blue-700 hover:text-blue-800"
          >
            {doc.title}
          </button>
        ) : (
          <span className="text-slate-500">
            {doc.title}
            {doc.pending ? ' · uploading…' : ''}
          </span>
        ),
    },
    {
      key: 'size',
      label: 'Size',
      widthClass: 'w-24',
      sortValue: (doc) => doc.size ?? 0,
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (doc) => formatBytes(doc.size),
    },
    {
      key: 'uploadedBy',
      label: 'Uploaded by',
      widthClass: 'w-40',
      filter: 'text',
      filterValue: (doc) => doc.uploadedByName,
      sortValue: (doc) => doc.uploadedByName.toLowerCase(),
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (doc) => doc.uploadedByName || '—',
    },
    {
      key: 'added',
      label: 'Added',
      widthClass: 'w-32',
      sortValue: (doc) => doc.createdAt,
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (doc) => formatRelativeDateTime(doc.createdAt),
    },
    {
      key: 'actions',
      label: '',
      widthClass: 'w-36',
      render: (doc) =>
        onDeleteDocument ? (
          confirmingDeleteId === doc.id ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="inline-flex h-7 items-center rounded-[7px] border border-rose-300 bg-rose-50 px-2.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                onClick={(event) => {
                  event.stopPropagation()
                  onDeleteDocument(doc.id)
                  setConfirmingDeleteId(null)
                }}
              >
                Delete
              </button>
              <button
                type="button"
                className="inline-flex h-7 items-center rounded-[7px] border border-slate-300 bg-white px-2.5 text-xs text-slate-600 hover:bg-slate-50"
                onClick={(event) => {
                  event.stopPropagation()
                  setConfirmingDeleteId(null)
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="inline-flex h-7 items-center rounded-[7px] border border-slate-300 bg-white px-2.5 text-xs text-slate-600 hover:bg-slate-50"
              onClick={(event) => {
                event.stopPropagation()
                setConfirmingDeleteId(doc.id)
              }}
            >
              Delete…
            </button>
          )
        ) : null,
    },
  ]

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="max-w-container mx-auto px-4 pb-6 pt-0 space-y-4">
        <section className="relative left-1/2 right-1/2 -mx-[50vw] w-screen rounded-none border-y border-slate-200 bg-slate-100 pb-0 pt-3">
          <div className="mx-auto max-w-container px-4">
            <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <nav aria-label="Breadcrumb" className="text-lg font-semibold text-slate-800">
                  <ol className="flex items-center gap-1">
                    <li>
                      <Link to="/customers" className="text-blue-700 hover:underline">
                        Customers
                      </Link>
                    </li>
                    <li aria-hidden="true">/</li>
                    <li>{customer.publicId ?? customer.id}</li>
                  </ol>
                </nav>

                <h1 className="text-xl font-semibold text-slate-900">{customer.fullName}</h1>
              </div>
              {customer.archivedAt ? (
                onRestore ? (
                  <button
                    type="button"
                    onClick={() => void onRestore()}
                    className="h-8 cursor-pointer rounded-lg border border-slate-300 bg-white px-3 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Restore customer
                  </button>
                ) : null
              ) : onArchive ? (
                <button
                  type="button"
                  onClick={onArchive}
                  className="h-8 cursor-pointer rounded-lg border border-rose-200 bg-white px-3 text-[12.5px] font-medium text-rose-600 hover:bg-rose-50"
                >
                  Archive customer
                </button>
              ) : null}
            </header>
            {customer.archivedAt ? (
              <div
                role="status"
                className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                <strong>Archived</strong> {formatShortRelativeDateTime(customer.archivedAt)}
                {customer.archivedBy && getAuthorDisplayName
                  ? ` by ${getAuthorDisplayName(customer.archivedBy)}`
                  : ''}
                {customer.archiveReason ? ` — ${customer.archiveReason}` : ''}
                {mergedInto ? (
                  <>
                    . Resources were relinked to{' '}
                    <Link
                      to="/customers/$id"
                      params={{ id: mergedInto.routeId }}
                      className="font-medium underline"
                    >
                      {mergedInto.name}
                    </Link>
                    .
                  </>
                ) : null}
              </div>
            ) : null}

            <nav aria-label="Customer tabs" className="relative mt-3">
              <div className="pointer-events-none absolute bottom-0 left-1/2 right-1/2 -mx-[50vw] w-screen border-b border-slate-300" />
              <div className="relative z-10 flex flex-wrap items-end gap-1">
                <Link
                  to="/customers/$id"
                  params={{ id: customerRouteId }}
                  className={
                    activeTab === 'details'
                      ? 'rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 -mb-px'
                      : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200'
                  }
                >
                  Details
                </Link>
                <Link
                  to="/customers/$id/services"
                  params={{ id: customerRouteId }}
                  className={
                    activeTab === 'services'
                      ? 'rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 -mb-px'
                      : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200'
                  }
                >
                  Services
                </Link>
                <Link
                  to="/customers/$id/notes"
                  params={{ id: customerRouteId }}
                  className={
                    activeTab === 'notes'
                      ? 'rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 -mb-px'
                      : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200'
                  }
                >
                  Notes
                </Link>
                <Link
                  to="/customers/$id/documents"
                  params={{ id: customerRouteId }}
                  className={
                    activeTab === 'documents'
                      ? 'rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 -mb-px'
                      : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200'
                  }
                >
                  Documents
                </Link>
                <Link
                  to="/customers/$id/history"
                  params={{ id: customerRouteId }}
                  className={
                    activeTab === 'history'
                      ? 'rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 -mb-px'
                      : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200'
                  }
                >
                  History
                </Link>
              </div>
            </nav>
          </div>
        </section>

        {activeTab === 'details' ? (
          <>
          <section className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
              <FormCapsLabel>Customer details</FormCapsLabel>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={resetButtonClasses(customerDraftDirty && !isSavingCustomer)}
                  disabled={!customerDraftDirty || isSavingCustomer}
                  onClick={handleResetCustomerDetails}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className={saveButtonClasses(customerDraftDirty && !isSavingCustomer)}
                  disabled={!customerDraftDirty || isSavingCustomer}
                  onClick={handleSaveCustomerDetails}
                >
                  {isSavingCustomer ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Name</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.fullName}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, fullName: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Omni ID</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.externalId}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, externalId: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Email</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.contactEmail}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, contactEmail: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Phone</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.contactPhone}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, contactPhone: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-start gap-3">
                <FormFieldLabel className="min-w-[7rem]">Address</FormFieldLabel>
                <UnderlineTextarea
                  className="w-auto flex-1"
                  rows={2}
                  value={customerDraft.contactAddress}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, contactAddress: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Invoice to</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.invoiceName}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, invoiceName: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Invoice email</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.invoiceEmail}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, invoiceEmail: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">Invoice phone</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.invoicePhone}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, invoicePhone: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-start gap-3">
                <FormFieldLabel className="min-w-[7rem]">Invoice address</FormFieldLabel>
                <UnderlineTextarea
                  className="w-auto flex-1"
                  rows={2}
                  value={customerDraft.invoiceAddress}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, invoiceAddress: event.target.value }))
                  }
                />
              </div>
              <div className="flex items-end gap-3">
                <FormFieldLabel className="min-w-[7rem]">VAT</FormFieldLabel>
                <UnderlineInput
                  className="w-auto flex-1"
                  value={customerDraft.invoiceVat}
                  onChange={(event) =>
                    setCustomerDraft((prev) => ({ ...prev, invoiceVat: event.target.value }))
                  }
                />
              </div>
            </div>
          </section>
          <RevisionsCard
            revisions={revisions}
            getAuthorDisplayName={getAuthorDisplayName ?? ((id) => id)}
          />
          </>
        ) : activeTab === 'notes' ? (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-xl font-semibold text-slate-900">
                Customer Notes
              </h1>
              {onCreateFollowUp ? (
                <button
                  type="button"
                  className="inline-flex h-8 items-center rounded-lg border border-violet-300 bg-white px-3 text-[12px] font-medium text-violet-700 hover:bg-violet-50"
                  onClick={() => setIsCreatingFollowUp(true)}
                >
                  + Follow-up
                </button>
              ) : null}
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-4 max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:p-0">
            <CustomerConversation
              customerId={customer.id}
              customerName={customer.fullName}
              followUpThreads={followUpThreads}
              generalNotes={generalNotes}
              selectedThreadKey={selectedThreadKey}
              onSelectThread={onSelectThread}
              newNoteAuthorName={newNoteAuthorName}
              onAddNote={onAddNote}
              onSubmitPhotoNote={onSubmitPhotoNote}
              onReplacePhotoNote={onReplacePhotoNote}
              onSaveEditNote={onSaveEditNote}
              onDeleteNote={onDeleteNote}
              photoNoteError={photoNoteError}
              onClearPhotoNoteError={onClearPhotoNoteError}
              onCompleteFollowUp={onCompleteFollowUp}
              onSnoozeFollowUp={onSnoozeFollowUp}
              onRescheduleFollowUp={onRescheduleFollowUp}
              onCloseFollowUp={onCloseFollowUp}
              onReopenFollowUp={onReopenFollowUp}
              onOpenHistory={onOpenHistory}
            />
            </div>
            {isCreatingFollowUp && onCreateFollowUp ? (
              <NewFollowUpDialog
                initialCustomer={{ id: customer.id, name: customer.fullName }}
                assigneeOptions={assigneeOptions}
                categoryOptions={categoryOptions}
                onCancel={() => setIsCreatingFollowUp(false)}
                onCreate={(input, extras) => {
                  onCreateFollowUp(input, extras)
                  setIsCreatingFollowUp(false)
                }}
              />
            ) : null}
          </section>
        ) : activeTab === 'documents' ? (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-xl font-semibold text-slate-900">
                Documents
              </h1>
              {onUploadDocuments ? (
                <>
                  <input
                    ref={documentInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(event) => {
                      const files = Array.from(event.target.files ?? [])
                      if (files.length > 0) onUploadDocuments(files)
                      // Reset so picking the same file again re-fires change.
                      event.target.value = ''
                    }}
                  />
                  <button
                    type="button"
                    className="inline-flex h-8 items-center rounded-lg border border-blue-400 bg-blue-500 px-3 text-[12.5px] font-medium whitespace-nowrap text-white hover:bg-blue-600"
                    onClick={() => documentInputRef.current?.click()}
                  >
                    + Upload
                  </button>
                </>
              ) : null}
            </div>
            {documentError ? (
              <p className="flex items-center gap-2 text-xs text-rose-600">
                {documentError}
                {onClearDocumentError ? (
                  <button
                    type="button"
                    className="cursor-pointer underline"
                    onClick={onClearDocumentError}
                  >
                    Dismiss
                  </button>
                ) : null}
              </p>
            ) : null}
            <LedgerTable
              rows={documents}
              columns={documentColumns}
              rowKey={(doc) => doc.id}
              itemLabelPlural="documents"
              emptyLabel="No documents for this customer yet. Upload quotes, letters or signed forms to keep them here."
              defaultSort={{ key: 'added', dir: 'desc' }}
              paginated={false}
              autoHeight
              minTableWidthClass="min-w-[680px]"
              mobileCard={(doc) => (
                <div className="space-y-1.5 px-3.5 py-3">
                  <div className="flex items-center justify-between gap-2">
                    {doc.fileUrl && onOpenDocument ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          onOpenDocument(doc)
                        }}
                        className="min-w-0 cursor-pointer truncate text-left text-[13px] font-medium text-blue-700 underline"
                      >
                        {doc.title}
                      </button>
                    ) : (
                      <span className="min-w-0 truncate text-[13px] text-slate-500">
                        {doc.title}
                        {doc.pending ? ' · uploading…' : ''}
                      </span>
                    )}
                    <span className="shrink-0 text-[12px] text-slate-400">
                      {formatBytes(doc.size)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[12px] text-slate-500">
                    <span className="min-w-0 truncate">
                      {doc.uploadedByName}
                    </span>
                    <span className="shrink-0">
                      {formatRelativeDateTime(doc.createdAt)}
                    </span>
                  </div>
                </div>
              )}
            />
          </section>
        ) : activeTab === 'history' ? (
          <section className="space-y-2">
            <h1 className="text-xl font-semibold text-slate-900">History</h1>
            <div className="rounded-md border border-slate-200 bg-white p-4 max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:p-0">
            <CustomerHistory
              events={historyEvents}
              onOpenLink={onOpenHistoryLink}
            />
            </div>
          </section>
        ) : (
          <section className="space-y-2">
            {archivedServicesError ? (
              <p className="text-xs text-rose-600">
                Archived services could not be loaded — showing active services
                only.
              </p>
            ) : null}

            <LedgerTable
              rows={services}
              columns={serviceColumns}
              rowKey={(service) => service.id}
              itemLabelPlural="services"
              title="Services"
              emptyLabel="No callouts, sessions, or jobs captured for this customer yet."
              onRowClick={openService}
              defaultSort={{ key: 'booked', dir: 'desc' }}
              paginated={false}
              autoHeight
              minTableWidthClass="min-w-[680px]"
              mobileFilterKeys={['type', 'status']}
              mobileCard={(service) => (
                <div className="space-y-1.5 px-3.5 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="font-mono text-[12px] text-slate-500">
                        {service.id}
                      </span>
                      <LedgerBadge tone={serviceTypeTone[service.serviceType]}>
                        {service.serviceType}
                      </LedgerBadge>
                    </div>
                    <span className="shrink-0 text-[12px] text-slate-400">
                      {bookedLabel(service)}
                    </span>
                  </div>
                  <div className="line-clamp-2 text-[13px] leading-snug text-slate-700">
                    {service.issueSummary || '—'}
                  </div>
                  <div className="pt-0.5">
                    <StatusHistoryPill
                      variant="ledger"
                      currentStatus={service.status}
                      statusChanges={service.statusChanges}
                      getAuthorDisplayName={getAuthorDisplayName}
                    />
                  </div>
                </div>
              )}
            />
          </section>
        )}
      </main>
    </div>
  )
}
